import { useEffect, useState } from "react";
import { api, formatNaira } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";

const COLORS = ["#00A8A8", "#1E2A38", "#F59E0B", "#8B5CF6", "#EF4444", "#10B981"];

function Bar({ label, value, max, color = "#00A8A8" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-700 dark:text-slate-300 font-medium">{label}</span>
        <span className="num text-slate-900 dark:text-slate-100 font-semibold">{typeof value === "number" && value > 1000 ? formatNaira(value) : value}</span>
      </div>
      <div className="h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function Donut({ data, title }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0) || 1;
  let cumulative = 0;
  const size = 160;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const entries = Object.entries(data);
  return (
    <div className="kpi-card">
      <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 mb-4">{title}</div>
      <div className="flex items-center gap-6">
        <svg width={size} height={size} className="shrink-0">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
          {entries.map(([key, val], i) => {
            const pct = val / total;
            const dash = pct * c;
            const el = (
              <circle key={key} cx={size/2} cy={size/2} r={r} fill="none"
                      stroke={COLORS[i % COLORS.length]} strokeWidth={stroke}
                      strokeDasharray={`${dash} ${c - dash}`}
                      strokeDashoffset={-cumulative * c}
                      transform={`rotate(-90 ${size/2} ${size/2})`}
                      className="transition-all" />
            );
            cumulative += pct;
            return el;
          })}
        </svg>
        <div className="space-y-2 flex-1">
          {entries.map(([key, val], i) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <span className="text-slate-700 dark:text-slate-300 capitalize flex-1">{key.replace("_", " ")}</span>
              <span className="num font-medium text-slate-900 dark:text-slate-100">{val}</span>
            </div>
          ))}
          {entries.length === 0 && <div className="text-xs text-slate-400">No data</div>}
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-400 text-sm">Loading analytics…</div>;

  const maxRevenue = Math.max(...Object.values(data.revenue_by_unit || {}), 1);
  const maxPipeline = Math.max(...Object.values(data.pipeline_value || {}), 1);

  return (
    <div className="space-y-6">
      <PageHeader title="Analytics" subtitle="Live data across FigureWeb, FigureCX, and Telecom" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Revenue</div><div className="font-heading font-bold text-3xl num text-emerald-600">{formatNaira(data.total_revenue)}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Expenses</div><div className="font-heading font-bold text-3xl num text-red-500">{formatNaira(data.total_expenses)}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Net Profit</div><div className={`font-heading font-bold text-3xl num ${data.profit >= 0 ? "text-slate-900 dark:text-slate-100" : "text-red-500"}`}>{formatNaira(data.profit)}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="kpi-card">
          <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 mb-4">Revenue by Business Unit</div>
          <div className="space-y-4">
            {Object.entries(data.revenue_by_unit).map(([unit, amt], i) => (
              <Bar key={unit} label={unit} value={amt} max={maxRevenue} color={COLORS[i]} />
            ))}
          </div>
        </div>
        <div className="kpi-card">
          <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 mb-4">Pipeline Value by Stage</div>
          <div className="space-y-4">
            {Object.entries(data.pipeline_value).map(([stage, val], i) => (
              <Bar key={stage} label={stage.replace("_", " ")} value={val} max={maxPipeline} color={COLORS[i]} />
            ))}
          </div>
        </div>
        <Donut data={data.invoice_status} title="Invoice Status" />
        <Donut data={data.ticket_priority} title="Open Tickets by Priority" />
      </div>
    </div>
  );
}
